import React from 'react';
import PropTypes from 'prop-types';
import { LuBuilding, LuCheck, LuFolderKanban, LuLoaderCircle, LuUserRound } from 'react-icons/lu';
import { CARD, EmptyState, SectionHeader } from '../../ProfilePage/components/ProfileUi';
import useSmoText from '../smoText';

/**
 * SMO cards. Admins see every SMO; other users see their own SMO plus the
 * views they get as a backup ("Yedek": one project, "Merkez Yedeği": the
 * centres where they are the backup SMO). A single view is opened
 * automatically, so the list is hidden then.
 */
const SmoPicker = ({ isAdmin, loadingSMOs, smoList, employeeId, delegatedSmoViews, centerBackupViews, selectedSMO, onSelect }) => {
  const { label } = useSmoText();

  const header = (
    <SectionHeader
      icon={LuUserRound}
      gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30"
      title={label('picker.title')}
      description={label('picker.subtitle')}
    />
  );

  if (isAdmin && loadingSMOs) {
    return (
      <section className={`${CARD} tw-space-y-4 tw-p-4 sm:tw-p-5`}>
        {header}
        <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-8 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
          <LuLoaderCircle aria-hidden="true" className="tw-h-5 tw-w-5 tw-animate-spin tw-text-teal-500" />
          {label('picker.loading')}
        </p>
      </section>
    );
  }

  // Build the list of selectable SMO views.
  // - Admin: full smoList.
  // - Non-admin: own SMO (if any) + delegated backup-SMO-Lead views.
  let visibleList = [];
  if (isAdmin) {
    visibleList = smoList.map((s) => ({ ...s, viewKey: s.id }));
  } else {
    const ownSmo = smoList.find((s) => s.id === employeeId);
    if (ownSmo) visibleList.push({ ...ownSmo, viewKey: ownSmo.id });
    visibleList = visibleList.concat(delegatedSmoViews, centerBackupViews);
  }
  // Hide picker entirely when there's nothing to pick OR when a
  // non-admin has exactly one entry (auto-selected, no need to show).
  if (visibleList.length === 0) {
    return isAdmin ? (
      <section className={`${CARD} tw-space-y-4 tw-p-4 sm:tw-p-5`}>
        {header}
        <EmptyState icon={LuUserRound} title={label('picker.empty')} />
      </section>
    ) : null;
  }
  if (!isAdmin && visibleList.length === 1) return null;

  const currentKey = selectedSMO?.viewKey || selectedSMO?.id;
  const hasDelegated = visibleList.some((smo) => smo.delegatedFromBackup || smo.delegatedFromCenterBackup);

  return (
    <section className={`${CARD} tw-space-y-4 tw-p-4 sm:tw-p-5`}>
      {header}
      <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2 lg:tw-grid-cols-3 2xl:tw-grid-cols-4">
        {visibleList.map((smo) => {
          const key = smo.viewKey || smo.id;
          const isProjectBackup = !!smo.delegatedFromBackup;
          const isCenterBackup = !!smo.delegatedFromCenterBackup;
          const isDelegated = isProjectBackup || isCenterBackup;
          const selected = currentKey === key;
          let tooltip;
          if (isProjectBackup) tooltip = label('picker.projectBackupHint', { project: smo.restrictToProjectTitle || '' });
          else if (isCenterBackup) tooltip = label('picker.centerBackupHint', { count: smo.centerCount });
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelect(smo)}
              title={tooltip}
              aria-pressed={selected}
              className={`tw-relative tw-flex tw-min-w-0 tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-p-3.5 tw-text-left tw-transition focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                selected
                  ? 'tw-border-teal-500/60 tw-bg-teal-500/[0.08] tw-shadow-[0_14px_30px_-22px_rgba(13,148,136,0.7)] dark:tw-border-teal-400/50 dark:tw-bg-teal-400/10'
                  : 'tw-border-slate-200/80 tw-bg-white/70 hover:-tw-translate-y-0.5 hover:tw-border-teal-500/40 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.06]'
              }`}
            >
              <span className="tw-grid tw-h-11 tw-w-11 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-sky-500 tw-to-blue-700 tw-text-white">
                <LuUserRound aria-hidden="true" className="tw-h-5 tw-w-5" />
              </span>
              <span className="tw-min-w-0 tw-flex-1">
                <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                  <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{smo.name} {smo.surname}</span>
                  {isDelegated && (
                    <span className="tw-rounded-full tw-bg-amber-400/20 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-amber-800 dark:tw-text-amber-200">
                      {isCenterBackup ? label('picker.centerBackup') : label('picker.backup')}
                    </span>
                  )}
                </span>
                {isProjectBackup ? (
                  <span className="tw-mt-0.5 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-orange-700 dark:tw-text-orange-300">
                    <LuFolderKanban aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                    {smo.restrictToProjectTitle || label('picker.project')}
                  </span>
                ) : (
                  <span className="tw-mt-0.5 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                    <LuBuilding aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                    {label('picker.centers', { count: smo.centerCount })}
                  </span>
                )}
                {smo.email && <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{smo.email}</span>}
              </span>
              {selected && (
                <span className="tw-grid tw-h-6 tw-w-6 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-teal-500 tw-text-white">
                  <LuCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
      </div>
      {hasDelegated && <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('picker.delegatedLegend')}</p>}
      <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('picker.toggleHint')}</p>
    </section>
  );
};

SmoPicker.propTypes = {
  isAdmin: PropTypes.bool,
  loadingSMOs: PropTypes.bool,
  smoList: PropTypes.array.isRequired,
  employeeId: PropTypes.string,
  delegatedSmoViews: PropTypes.array.isRequired,
  centerBackupViews: PropTypes.array.isRequired,
  selectedSMO: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
};

export default SmoPicker;
